// 3rd party
import { max, min } from 'lodash';

// Components
import { getSharedTooltip } from '@/views/components/charts/utils/tooltip';
import { getGrowthChartOptions } from '@/views/components/charts/growth-chart/utils/chart-options';

// Utilities
import { makeHistoricalScenarioTooltipFormatter } from './tooltip';
import { generateHistoricalScenariosPlotBands, generateScenarioEdgeMarkers, renderPlotBandBrackets } from '@/utils/historical-scenarios/plot-bands';
import { PERFORMANCE_CHART } from '@/utils/constants';
import { getDefaultChartOptions } from '../../../Charts/utils/drawdown-chart-helpers';
import { getEarliestAndLatestDatesFromSeries } from '@/utils/disclaimers/date-helpers/date-helpers';
import { LABEL_FORMATTERS } from '@/utils/charts';
import { calculateRiskRewardAxes, getRiskRewardChartOptions } from '../../../Charts/utils/risk-reward-chart-helpers';

// Types
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import HistoricalScenario from '@/types/historical-scenario';
import { ScatterPlotDatum, ScatterPlotOptions } from '@/views/components/charts/scatter-plot/ScatterPlot';

export const getHistoricalScenariosChartOptions = (
  options: { height: Nullable<number> | undefined; locale: Locale; animate: boolean },
  selectedScenarios: HistoricalScenario[] = [],
  selectedChart: PERFORMANCE_CHART,
  data?: ScatterPlotDatum[],
) => {
  const plotBands = generateHistoricalScenariosPlotBands(selectedScenarios);
  const plotLines = generateScenarioEdgeMarkers(selectedScenarios);

  const optionsOverride = {
    tooltip: getSharedTooltip(makeHistoricalScenarioTooltipFormatter(options.locale, selectedScenarios, selectedChart)),
    chart: {
      events: {
        render(this: any) {
          renderPlotBandBrackets(this, selectedScenarios);
        },
      },
    },
    xAxis: {
      plotBands,
      plotLines,
    },
  };

  switch (selectedChart) {
    case PERFORMANCE_CHART.DRAWDOWN: {
      return getDefaultChartOptions({ height: options.height, yAxisOpposite: false, animate: options.animate || true, optionsOverrides: optionsOverride });
    }

    case PERFORMANCE_CHART.RISK_REWARD: {
      const axes = calculateRiskRewardAxes(data!, 600, 400);
      const scatterPlotChartOptions: ScatterPlotOptions = getRiskRewardChartOptions(axes);

      return scatterPlotChartOptions;
    }

    default:
      return getGrowthChartOptions({ height: options.height, locale: options.locale, animate: options.animate || true }, optionsOverride);
  }
};

export function getScenarioOrSeriesDateRange(
  selectedScenarios: HistoricalScenario[] | undefined,
  seriesData: Highcharts.SeriesOptionsType[],
): { startDate: Date | null; endDate: Date | null } {
  if (selectedScenarios?.length) {
    const starts = selectedScenarios.map(s => new Date(s.startDate!));
    const ends = selectedScenarios.map(s => new Date(s.endDate!));

    return {
      startDate: min(starts) ?? null,
      endDate: max(ends) ?? null,
    };
  }
  // Defaults to earliest and latest dates
  const { startDate, endDate } = getEarliestAndLatestDatesFromSeries(seriesData);

  return { startDate, endDate };
}

export function getXAxisFormatter(locale: Locale, xAxis?: Highcharts.XAxisOptions | Highcharts.XAxisOptions[]) {
  const xAxisConfig = Array.isArray(xAxis) ? (xAxis[0] ?? {}) : (xAxis ?? {});
  return {
    ...xAxisConfig,
    labels: {
      ...xAxisConfig.labels,
      formatter: LABEL_FORMATTERS.date[locale].formatter,
    },
  };
}
